// Leaving a room with work in it.
//
// A room keeps no history: close the tab with changes nobody saved and they
// are gone. `beforeunload` covers the tab closing and the address bar. It does
// NOT cover the two ways people actually leave — the page's own back link, and
// the browser's Back button — because both are in-app navigation, which never
// unloads anything.
//
// React Router's own blocker needs a data router (createBrowserRouter); this
// app mounts <BrowserRouter>, so the guard is built from the history API:
// while there is unsaved work, a sentinel entry sits on top of the stack, and
// a Back press lands on it rather than off the page. If the person means it,
// the sentinel is dropped and they go; if not, it is put back.
//
// Every other way out — the back link, a jam started from the room — goes
// through `ask`, which drops the sentinel BEFORE the caller navigates. Popping
// it afterwards used to land the Back press on the sentinel a second time
// (the dialog asked again) and then walk history back instead of going where
// the link pointed — in a fresh tab, nowhere at all.
//
// The confirmation is a branded React modal (UnsavedDialog) rendered by the
// room, not the browser's native confirm(). On mobile the native dialog is
// ugly and disorienting; the branded one matches the app.

import { useCallback, useEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { useNavigate } from "react-router";

export interface UnsavedGuard {
  /**
   * For the page's own back link: `onClick={(e) => guard.confirmLeave(e, to)}`.
   * Lets the click through when there is nothing to lose. Otherwise blocks
   * it, asks, and goes to `to` if the person means it.
   */
  confirmLeave: (e: MouseEvent, to: string) => void;
  /**
   * Ask, for a caller that leaves by its own means: true to go ahead. Resolves
   * only once the Back sentinel is off the stack, so the caller's navigation
   * lands on top of the room's own entry.
   */
  ask: () => Promise<boolean>;
  /** The branded dialog element to render in the room's JSX. */
  dialog: ReactNode;
}

/** The sentinel's marker, so a Back press can be told from any other. */
const SENTINEL = { seedbedUnsavedGuard: true } as const;

function isSentinel(state: unknown): boolean {
  return typeof state === "object" && state !== null && (state as { seedbedUnsavedGuard?: unknown }).seedbedUnsavedGuard === true;
}

/** How long to wait for the browser to report the sentinel popped. */
const POP_TIMEOUT_MS = 500;

export function useUnsavedGuard(dirty: boolean, message: string): UnsavedGuard {
  const navigate = useNavigate();
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const messageRef = useRef(message);
  messageRef.current = message;

  // Whether a Back press that pops the sentinel should ask. Off once the
  // person has said to leave, and while the guard itself is popping it.
  const armedRef = useRef(false);
  // The pop in flight, if any: a second request joins it rather than popping
  // a second entry.
  const droppingRef = useRef<Promise<void> | null>(null);

  // Pending confirmation: a resolve callback that the dialog will call.
  const [pending, setPending] = useState<{ resolve: (leave: boolean) => void } | null>(null);

  /** The dialog's answer — or yes at once, when there is nothing to lose. */
  const confirm = useCallback((): Promise<boolean> => {
    if (!dirtyRef.current) return Promise.resolve(true);
    return new Promise<boolean>((resolve) => setPending({ resolve }));
  }, []);

  /**
   * Take the sentinel off the stack if it is the current entry. Resolves once
   * the browser has moved (history.back() is asynchronous), or after a short
   * wait should it never say.
   */
  const dropSentinel = useCallback((): Promise<void> => {
    if (droppingRef.current) return droppingRef.current;
    if (!isSentinel(window.history.state)) return Promise.resolve();
    armedRef.current = false;
    const dropping = new Promise<void>((resolve) => {
      let timer = 0;
      const done = () => {
        window.removeEventListener("popstate", done);
        window.clearTimeout(timer);
        droppingRef.current = null;
        resolve();
      };
      timer = window.setTimeout(done, POP_TIMEOUT_MS);
      window.addEventListener("popstate", done);
      window.history.back();
    });
    droppingRef.current = dropping;
    return dropping;
  }, []);

  const ask = useCallback(async (): Promise<boolean> => {
    const leave = await confirm();
    if (leave) await dropSentinel();
    return leave;
  }, [confirm, dropSentinel]);

  const confirmLeave = useCallback(
    (e: MouseEvent, to: string) => {
      // Nothing to lose and no sentinel to clear: the link navigates itself.
      if (!dirtyRef.current && !isSentinel(window.history.state)) return;
      e.preventDefault();
      void ask().then((leave) => {
        if (leave) navigate(to);
      });
    },
    [ask, navigate],
  );

  // The tab closing, a reload, an address typed over this one.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  // The Back button sentinel.
  useEffect(() => {
    if (!dirty) return;
    window.history.pushState(SENTINEL, "");
    armedRef.current = true;

    const onPopState = () => {
      // Landing ON the sentinel (a Forward press) is not leaving.
      if (!armedRef.current || isSentinel(window.history.state)) return;
      if (!dirtyRef.current) {
        armedRef.current = false;
        window.history.back();
        return;
      }
      // The sentinel is already gone: a yes goes back once more, to the page
      // before the room; a no puts the sentinel back.
      void confirm().then((leave) => {
        if (leave) {
          armedRef.current = false;
          window.history.back();
        } else {
          window.history.pushState(SENTINEL, "");
        }
      });
    };

    window.addEventListener("popstate", onPopState);
    return () => {
      armedRef.current = false;
      window.removeEventListener("popstate", onPopState);
      // Clean again (saved, or undone to where it started), or unmounting
      // with the sentinel still on top: take it off.
      void dropSentinel();
    };
  }, [dirty, confirm, dropSentinel]);

  const dialog = pending
    ? UnsavedDialog({
        message: messageRef.current,
        onLeave: () => { setPending(null); pending.resolve(true); },
        onStay: () => { setPending(null); pending.resolve(false); },
      })
    : null;

  return { confirmLeave, ask, dialog };
}

function UnsavedDialog({
  message,
  onLeave,
  onStay,
}: {
  message: string;
  onLeave: () => void;
  onStay: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onStay} />
      <div className="relative mx-4 w-full max-w-sm rounded-2xl border border-white/10 bg-[#141419] p-6 shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
        <div className="flex items-center gap-3 mb-4">
          <div className="size-10 rounded-xl bg-[var(--changed)]/15 flex items-center justify-center shrink-0">
            <svg className="size-5 text-[var(--changed)]" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M12 9v4M12 17h.01" />
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-bold">Unsaved changes</h3>
          </div>
        </div>
        <p className="text-sm text-[#8a8a8e] leading-relaxed">{message}</p>
        <div className="mt-5 flex gap-2">
          <button
            type="button"
            onClick={onStay}
            className="flex-1 h-11 rounded-xl bg-[var(--primary)] text-[var(--primary-foreground)] font-bold text-sm"
          >
            Keep editing
          </button>
          <button
            type="button"
            onClick={onLeave}
            className="flex-1 h-11 rounded-xl border border-white/15 text-sm font-semibold text-[var(--danger-soft)]"
          >
            Leave anyway
          </button>
        </div>
      </div>
    </div>
  );
}
